import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import {
  IPagination,
  Question,
  QuestionCreateRequest,
  QuestionSearchRequest,
  QuestionType,
  SupportedQuestionTypes,
} from '../../models';
import { AlertService, AlertType } from '../alert.service';

@Injectable({
  providedIn: 'root',
})
export class QuestionsService {
  // injects
  private readonly alertService = inject(AlertService);
  private readonly httpClient = inject(HttpClient);
  private readonly translate = inject(TranslateService);

  // api urls
  private readonly urlBase: string = 'api/performance-management/question';

  // signals
  filteredQuestions = signal<Question[]>([]);
  filteredQuestionsPaginated = signal<IPagination<Question>>({
    content: [],
    totalElements: 0,
    config: {
      sortColumns: [],
    },
  });
  questionTypes = signal<QuestionType[]>([]);
  questionTypesById = computed(() => {
    return this.questionTypes().reduce(
      (acc, questionType) => {
        acc[questionType.id] = questionType;
        return acc;
      },
      {} as Record<number, QuestionType>,
    );
  });

  private readonly unselectableQuestionTypes = new Set<SupportedQuestionTypes>([
    SupportedQuestionTypes.NUMERIC,
    SupportedQuestionTypes.MULTICHOICE,
  ]);

  selectableQuestionTypes = computed(() =>
    this.questionTypes().filter((type) => !this.unselectableQuestionTypes.has(type.name)),
  );

  // This will be temporary
  private readonly questionTypeDescriptions = new Map([
    [SupportedQuestionTypes.YES_NO, 'ADMIN.QUESTION_BANK.QUESTION_TYPES.YES_NO'],
    [SupportedQuestionTypes.NUMERIC, 'ADMIN.QUESTION_BANK.QUESTION_TYPES.NUMERIC'],
    [SupportedQuestionTypes.TEXT, 'ADMIN.QUESTION_BANK.QUESTION_TYPES.TEXT'],
    [SupportedQuestionTypes.DATE, 'ADMIN.QUESTION_BANK.QUESTION_TYPES.DATE'],
    [SupportedQuestionTypes.FREE_TEXT, 'ADMIN.QUESTION_BANK.QUESTION_TYPES.FREE_TEXT'],
    [SupportedQuestionTypes.DROPDOWN, 'ADMIN.QUESTION_BANK.QUESTION_TYPES.DROPDOWN'],
    [SupportedQuestionTypes.MULTICHOICE, 'ADMIN.QUESTION_BANK.QUESTION_TYPES.MULTICHOICE'],
  ]);

  constructor() {
    this.getAllQuestionTypes();
  }

  searchQuestions(request: QuestionSearchRequest): void {
    const payload = {
      search: request.search || null, // This can come as an empty string
      sectionId: request.sectionId,
      typeId: request.typeId,
    };

    this.httpClient
      .post<Question[]>(`${this.urlBase}/search`, payload)
      .pipe(catchError((error) => this.handleError(error)))
      .subscribe((response) => this.filteredQuestions.set(response));
  }

  filterQuestionsPaginated(request: QuestionSearchRequest): void {
    const props = {};

    if (request.search) props['search'] = request.search;
    if (request.sectionId) props['sectionId'] = request.sectionId;
    if (request.typeId) props['typeId'] = request.typeId;

    const params = request.params ? request.params.appendAll(props) : props;

    this.httpClient
      .get<IPagination<Question>>(`${this.urlBase}/overview`, { params })
      .pipe(catchError(this.handleError))
      .subscribe(this.filteredQuestionsPaginated.set);
  }

  getAllQuestionTypes() {
    this.httpClient
      .get<QuestionType[]>(`${this.urlBase}/list/question-types`)
      .pipe(
        catchError((error) => this.handleError(error)),
        tap((types) => {
          const mappedTypes = types
            .filter((qt) => Object.values(SupportedQuestionTypes).includes(qt.name))
            .map((type) => ({
              ...type,
              label:
                (this.questionTypeDescriptions.has(type.name) &&
                  this.translate.instant(this.questionTypeDescriptions.get(type.name))) ||
                type.name,
            }));
          this.questionTypes.set(mappedTypes);
        }),
      )
      .subscribe();
  }

  createQuestion(question: QuestionCreateRequest): Observable<Question> {
    return this.httpClient.post<Question>(`${this.urlBase}/create`, question).pipe(
      tap(() => this.handleSuccess(this.translate.instant('SHARED.SUCCESS'))),
      catchError((error) => this.handleError(error)),
    );
  }

  deleteById(id: number): Observable<Question> {
    return this.httpClient.delete<Question>(`${this.urlBase}/${id}`).pipe(
      tap(() => this.handleSuccess(this.translate.instant('SHARED.SUCCESS'))),
      catchError((error) => this.handleError(error)),
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.alertService.showAlert(error.error, AlertType.error);
    return throwError(() => error);
  }

  private handleSuccess(message: string) {
    this.alertService.showAlert(message, AlertType.success);
  }
}
